import type { AudioTaskType } from "../states";
import AudioWorker from "./audio.worker.ts?worker";
import type {
	WorkerRequest,
	WorkerRequestPayload,
	WorkerRequestType,
	WorkerResponse,
	WorkerResponseMap,
} from "./types";

type WorkerResponseValue = WorkerResponseMap[keyof WorkerResponseMap];

export interface WorkerUIHandlers {
	onTaskStart: (type: AudioTaskType) => void;
	onTaskEnd: () => void;
	onError: (error: string) => void;
	onTaskProgress: (progress: number) => void;
}

export class AudioWorkerClient {
	private worker: Worker;
	private fatalError: Error | null = null;

	private pendingRequests = new Map<
		number,
		{
			resolve: (value: WorkerResponseValue) => void;
			reject: (reason?: Error) => void;
			isCurrent: () => boolean;
		}
	>();

	private messageIdCounter = 0;
	private uiHandlers: WorkerUIHandlers;

	constructor(uiHandlers: WorkerUIHandlers) {
		this.uiHandlers = uiHandlers;
		this.worker = this.createWorker();
	}

	private createWorker() {
		const worker = new AudioWorker();

		worker.onmessage = (event) => {
			if (this.worker === worker) this.handleMessage(event);
		};
		worker.onerror = (e) => {
			if (this.worker !== worker) return;
			console.error("[Worker Error]", e);
			this.fatalError = new Error(e.message || "Audio worker failed");
			for (const pending of this.pendingRequests.values()) {
				pending.reject(this.fatalError);
			}
			this.pendingRequests.clear();
		};
		worker.onmessageerror = () => {
			if (this.worker !== worker) return;
			this.fatalError = new Error("Invalid audio worker response");
			for (const pending of this.pendingRequests.values())
				pending.reject(this.fatalError);
			this.pendingRequests.clear();
		};
		return worker;
	}

	public cancelPending() {
		if (this.pendingRequests.size === 0 && !this.fatalError) return;
		this.worker.terminate();
		for (const pending of this.pendingRequests.values())
			pending.reject(new DOMException("Audio load replaced", "AbortError"));
		this.pendingRequests.clear();
		this.fatalError = null;
		this.worker = this.createWorker();
	}

	private handleMessage(e: MessageEvent<WorkerResponse>) {
		const msg = e.data;
		if (msg.type === "CHUNK") return;

		if ("id" in msg) {
			const pending = this.pendingRequests.get(msg.id);

			if (pending) {
				let isFinished = false;

				if (msg.type === "ERROR") {
					pending.reject(new Error(msg.error));
					isFinished = true;
				} else if (msg.type === "METADATA") {
					pending.resolve(msg);
					isFinished = true;
				} else if (msg.type === "SEEK_DONE") {
					pending.resolve(msg.time);
					isFinished = true;
				} else if (msg.type === "EXPORT_WAV_DONE") {
					pending.resolve(msg.blob);
					isFinished = true;
				} else if (msg.type === "ACK") {
					pending.resolve(undefined);
					isFinished = true;
				} else if (msg.type === "EOF") {
					// 暂不处理
				} else if (msg.type === "EXPORT_WAV_PROGRESS") {
					if (pending.isCurrent()) this.uiHandlers.onTaskProgress(msg.progress);
				}

				if (isFinished) {
					this.pendingRequests.delete(msg.id);
				}
			}
		}
	}

	private postRequest<T extends WorkerRequestType>(
		type: T,
		payload: WorkerRequestPayload<T>,
		transfer: Transferable[] = [],
		isCurrent: () => boolean = () => true,
	): Promise<WorkerResponseMap[T]> {
		if (this.fatalError) return Promise.reject(this.fatalError);
		const id = this.messageIdCounter++;

		return new Promise((resolve, reject) => {
			this.pendingRequests.set(id, {
				resolve: resolve as (value: WorkerResponseValue) => void,
				reject,
				isCurrent,
			});

			const req = { type, id, ...payload } as WorkerRequest;

			try {
				this.worker.postMessage(req, transfer);
			} catch (error) {
				this.pendingRequests.delete(id);
				reject(error);
			}
		});
	}

	public async transcodeToWav(
		file: File | Blob,
		isCurrent: () => boolean = () => true,
	): Promise<Blob> {
		const fileObj =
			file instanceof File
				? file
				: new File([file], "temp.bin", { type: file.type });

		if (isCurrent()) this.uiHandlers.onTaskStart("TRANSCODING");

		try {
			const blob = await this.postRequest(
				"EXPORT_WAV",
				{ file: fileObj },
				[],
				isCurrent,
			);
			if (isCurrent()) this.uiHandlers.onTaskEnd();
			return blob;
		} catch (error) {
			if (isCurrent())
				this.uiHandlers.onError(
					`Transcoding failed: ${error instanceof Error ? error.message : String(error)}`,
				);
			if (isCurrent()) this.uiHandlers.onTaskEnd();
			throw error;
		}
	}

	public readMetadata(file: File | Blob) {
		const fileObj =
			file instanceof File
				? file
				: new File([file], "audio.bin", { type: file.type });
		return this.postRequest("READ_METADATA", { file: fileObj });
	}

	public terminate() {
		this.fatalError = new Error("Worker terminated");
		this.worker.terminate();
		for (const [_, pending] of this.pendingRequests) {
			pending.reject(new Error("Worker terminated"));
		}
		this.pendingRequests.clear();
	}
}
